import React, { useEffect, useState } from 'react';
import { RefreshCw, UserPlus, X } from 'lucide-react';
import { attendanceAPI } from '../../services/api';
import useDialog from '../../hooks/useDialog';
import Select from '../Select';
import PunchCodeDialog from './PunchCodeDialog';

// Starting a colleague's attendance when they cannot get to their own account: pick the person, say
// why, and a code is made for them. They scan it and punch on their own phone, with their own face,
// so this begins a record rather than completing one.
//
// Anyone signed in may do this (owner, 2026-10-03). What keeps it honest is that the selfie is
// still theirs and the entry records who set it up and why.

export const REASONS = [
  { key: 'COMPUTER_NOT_WORKING', label: 'Their computer is not working' },
  { key: 'CANNOT_SIGN_IN', label: 'They cannot sign in' },
  { key: 'OTHER', label: 'Something else' },
];

const MarkForColleagueDialog = ({ onDone, onClose }) => {
  const [people, setPeople] = useState(null);
  // Whether they are arriving or leaving is asked here, never assumed: this screen belongs to
  // whoever is signed in, and their own shift says nothing about the colleague's.
  const [side, setSide] = useState('IN');
  const [userId, setUserId] = useState('');
  const [reason, setReason] = useState('COMPUTER_NOT_WORKING');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [showing, setShowing] = useState(null); // { id, name, reason, reasonNote } once the code is up
  const dialogRef = useDialog(() => onClose(), { confirmDiscard: false });

  useEffect(() => {
    let live = true;
    attendanceAPI.colleagues()
      .then((rows) => { if (live) setPeople(Array.isArray(rows) ? rows : []); })
      .catch((e) => { if (live) { setPeople([]); setError(e?.message || 'The list of people could not be loaded.'); } });
    return () => { live = false; };
  }, []);

  const go = () => {
    if (!userId) { setError('Pick the person this is for.'); return; }
    if (reason === 'OTHER' && !note.trim()) { setError('Say why you are marking it for them.'); return; }
    const person = (people || []).find((p) => p.id === userId);
    setError('');
    setShowing({ id: userId, name: person?.fullName || '', reason, reasonNote: reason === 'OTHER' ? note.trim() : null });
  };

  if (showing) {
    return (
      <PunchCodeDialog
        side={side}
        forUser={showing}
        onDone={onDone}
        onClose={onClose}
      />
    );
  }

  return (
    <div ref={dialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-label="Mark attendance for a colleague">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 p-5 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <UserPlus className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-lg font-bold text-white">Mark for a colleague</h2>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600">
            They scan the code on their own phone and it takes their photo, so this only starts the
            record. Your name is kept on it.
          </p>

          <div>
            <span className="block text-sm font-semibold mb-1.5">Arriving or leaving</span>
            <div className="flex gap-2" role="group" aria-label="Arriving or leaving">
              {[['IN', 'Arriving'], ['OUT', 'Leaving']].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={side === key}
                  onClick={() => { setSide(key); setError(''); }}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium border ${side === key ? 'bg-navy-800 text-white border-navy-800' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {people === null ? (
            <p className="text-sm text-gray-500 flex items-center gap-2">
              <RefreshCw className="w-4 h-4 animate-spin" aria-hidden="true" /> Loading the list…
            </p>
          ) : (
            <Select
              label="Who is it for *"
              value={userId}
              onChange={(v) => { setUserId(v); setError(''); }}
              options={people.map((p) => ({ value: p.id, label: `${p.fullName}${p.role ? ` · ${p.role}` : ''}` }))}
              placeholder="Pick the person"
            />
          )}

          <div>
            <span className="block text-sm font-semibold mb-1.5">Why</span>
            <div className="flex flex-col gap-1.5" role="group" aria-label="Why">
              {REASONS.map((r) => (
                <label key={r.key} className="flex items-center gap-2.5 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="mark-reason"
                    checked={reason === r.key}
                    onChange={() => { setReason(r.key); setError(''); }}
                    className="h-4 w-4 border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span>{r.label}</span>
                </label>
              ))}
            </div>
            {reason === 'OTHER' && (
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={200}
                autoFocus
                aria-label="Why you are marking it for them"
                placeholder="In a few words"
                className="mt-2 w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
              />
            )}
          </div>

          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
        </div>

        <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2 rounded-b-2xl">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium">Cancel</button>
          <button type="button" onClick={go} disabled={people === null} className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            Show their code
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarkForColleagueDialog;
